import Grid from '@material-ui/core/Grid';
import AgencyAdministrationRouteConstants from 'AgencyAdministration/AgencyAdministrationRouteConstants';
import DashboardCard from 'Core/components/Dashboard/Card';
import 'Core/components/Dashboard/Dashboard.sass';
import {updatefeatureVariables} from 'Core/ducks/featureToggle';
import {showModal} from 'Core/ducks/modal';
import {useRedirectToRequestedPage} from 'Core/hooks/useRedirectToRequestedPage';
import {CustomerSupportRouteConstants} from 'CustomerSupport/Routes';
import * as O from 'fp-ts/lib/Option';
import {QuotingRouteConstants} from 'Quoting/Routes';
import React, {useEffect} from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router';
import {ReducerState} from 'rootReducer';
import {hasScope} from 'Shared/components/RequireScope';
import {AgentNumberStatusId} from 'Shared/constants/AgentNumberStatus';
import {Scope} from 'Shared/constants/Scope';
import {NoOp} from 'Shared/utilities/IOUtilities';
import sortElementsByKey from 'Shared/utilities/SortUtility';
import * as agentUser from 'Users/ducks/agentUser';
import {AgentClaims} from 'Users/models/AgentClaims';
import {AsyncUserClaims, UserTypeClaimKey, UserTypeClaims} from 'Users/models/Claims';
import {matchUserTypeClaimKey} from 'Users/models/User/UserType';
import {UserRouteConstants} from 'Users/Routes';
import {isAgencyInStatus, loadUserClaims, selectUserScopes} from 'Users/selectors/userProfileSelectors';
import {clearSearchQuote} from 'Quoting/ducks/quoteSearch';

type LineOfBusinessDashboardProps = {
  showModal: typeof showModal;
} & ReturnType<typeof mapStateToProps> & {userClaims: AsyncUserClaims} & RouteComponentProps;

export const LineOfBusinessDashboard = React.memo((props: LineOfBusinessDashboardProps) => {
  const {userClaims, history, scopes} = props;
  const userHasScope = hasScope(scopes);
  const claims = loadUserClaims(userClaims);
  const {selectedAgency} = useSelector((state: ReducerState) => state.user.profile);
  const isAgentAdmin = userHasScope(Scope.SearchAgentUsers) && !O.isSome(selectedAgency);
  const hasScopeForCustomerSupport = userHasScope(Scope.AgentSearchCustomerSupport);
  let [isImpersonatingAgentUser] = [false];

  let [hasAgencyAssociation] = [true];
  const {featureVariables} = useSelector((state: ReducerState) => state.featureToggle);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(updatefeatureVariables({...featureVariables, FOOTER_OPEN_DEFAULT: true}));
  }, []);

  useRedirectToRequestedPage();

  claims.userTypes.map(
    UserTypeClaims.match({
      [UserTypeClaimKey.AgentSite]: (agentClaims: AgentClaims) => {
        if (agentClaims.impersonatedAgentClaim) {
          isImpersonatingAgentUser = true;
          hasAgencyAssociation = agentClaims.impersonatedAgentClaim.agencyAssociations.length > 0;
        }
      },
      default: () => false,
    })
  );
  const isImpersonating = isImpersonatingAgentUser;
  const userTypeCount = claims.userTypes.length;
  const handleAgentDashboardTileClick = () => {
    if (isAgentAdmin) {
      dispatch(agentUser.clear());
      history.push(AgencyAdministrationRouteConstants.AGENTS);
    } else {
      history.push('/agent/agentDashboard');
    }
  };

  const handleQuotingDashboardTileClick = () => {
    dispatch(clearSearchQuote());
    history.push(QuotingRouteConstants.QUOTING_ROOT);
  };

  const handlePortalDashboardTileClick = () => {
    history.push(UserRouteConstants.USERS);
  };

  const lineOfBusinessDashboardCards = claims.userTypes.map(userType =>
    
    matchUserTypeClaimKey(userType, {
      
      [UserTypeClaimKey.AgentSite]: () => {
        if (
          !isImpersonating &&
          (claims.userTypes.filter(agent => agent.$type == UserTypeClaimKey.MyAccessPortal).length > 0
          || claims.userTypes.filter(agent => agent.$type == UserTypeClaimKey.Quoting).length > 0)
        ) {
          return (
            <Grid item lg={3} md={4} sm={6} xs={12} key={`grid-${UserTypeClaimKey.AgentSite}`}>
              <DashboardCard
                key={UserTypeClaimKey.AgentSite}
                image={() => <i className="fa fa-briefcase" />}
                title={`Agent${isAgentAdmin ? ' Administration' : ''}`}
                description={`Access${isAgentAdmin ? ' features for agent administration.' : ' broker resources.'}`}
                linkText="Agent"
                onClickLink={handleAgentDashboardTileClick}
              />
            </Grid>
          );
        } else {
          handleAgentDashboardTileClick();
          return <span key={UserTypeClaimKey.AgentSite} />;
        }
      },
      [UserTypeClaimKey.MyAccessPortal]: () => {
        if (!isImpersonating) {
          if (userTypeCount > 1) {
            return (
              <Grid item lg={3} md={4} sm={6} xs={12} key={`grid-${UserTypeClaimKey.MyAccessPortal}`}>
                <DashboardCard
                  key={UserTypeClaimKey.MyAccessPortal}
                  image={() => <i className="fas fa-fingerprint" />}
                  title="Portal"
                  description="Administer security in the MyAccess portal."
                  linkText="Enter IAM"
                  onClickLink={handlePortalDashboardTileClick}
                />
              </Grid>
            );
          } else {
            handlePortalDashboardTileClick();
            return <span key={UserTypeClaimKey.MyAccessPortal} />;
          }
        } else {
          return <span key="Unknown" />;
        }
      },
      [UserTypeClaimKey.Quoting]: () => {
        if (!isImpersonating) {
          if (userTypeCount > 1) {
            return (
              <Grid item lg={3} md={4} sm={6} xs={12} key={`grid-${UserTypeClaimKey.Quoting}`}>
                <DashboardCard
                  key={UserTypeClaimKey.Quoting}
                  image={() => <i className="fa fa-envelope" />}
                  title="Quoting"
                  description="Create Quotes for Access360 Products."
                  linkText="Quoting"
                  onClickLink={handleQuotingDashboardTileClick}
                />
              </Grid>
            );
          }
          else{
            handleQuotingDashboardTileClick()
            return <span key={UserTypeClaimKey.Quoting} />;
          }
        } else {
          return <span key="Unknown" />;
        }
      },
      Empty: () => <></>,
    })
  ).filter((element): element is JSX.Element => element !== undefined);

  const isUserAdmin =
    !isImpersonating &&
    lineOfBusinessDashboardCards.filter(
      dashboardCard => dashboardCard!.key === `grid-${UserTypeClaimKey.MyAccessPortal}`
    ).length === 0 &&
    userHasScope(Scope.SearchPortalUsers);

  const userAdministrationTile = isUserAdmin && <></>;

  const isCustomerSupport = !isImpersonating && hasScopeForCustomerSupport && isAgentAdmin;
  const customerSupportTile = isCustomerSupport && (
    <Grid item lg={3} md={4} sm={6} xs={12} key={`grid-${UserTypeClaimKey.AgentSite}`}>
      <DashboardCard
        key={'Customer Support'}
        image={() => <i className="fas fa-headset" />}
        title="Customer Support"
        description="Access features for customer support administration"
        linkText="Customer Support"
        onClickLink={() => {
          props.history.push(CustomerSupportRouteConstants.CUSTOMER_SUPPORT);
        }}
      />
    </Grid>
  );

  const orderedLineOfBusinessDashboardCards = sortElementsByKey(lineOfBusinessDashboardCards!);
  return (
    <div className="dashboard dashboard-lineOfBusiness">
      <Grid container spacing={4}>
        {orderedLineOfBusinessDashboardCards}
        {userAdministrationTile}
        {customerSupportTile}
      </Grid>
    </div>
  );
});

const mapStateToProps = (state: ReducerState) => ({
  scopes: selectUserScopes(state),
  isTerminatedAgent: isAgencyInStatus(state, AgentNumberStatusId.Terminated),
});

export default withRouter(
  connect(mapStateToProps, {
    showModal,
  })(LineOfBusinessDashboard)
);
